import { ActivatedRoute, ActivatedRouteSnapshot, CanDeactivate, MaybeAsync, RouterStateSnapshot } from '@angular/router';
import { Inject, Injectable, InjectionToken, Optional, inject } from '@angular/core';
import { Observable, of, from, isObservable, defer } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';

/**
 * Interface for the return value of canDeactivate functions.
 * Provides both the result and optional message to display.
 */
export interface CanDeactivateResult {
  /**
   * Whether the component can be deactivated.
   */
  canDeactivate: boolean;
  /**
   * Optional message to display if canDeactivate is false.
   * Overrides the default message.
   */
  message?: string;
}

/**
 * Interface that components must implement to use the PendingChangesGuard.
 * The canDeactivate method determines if navigation away from the component should be allowed.
 */
export interface ComponentCanDeactivate {
  /**
   * Method to determine if the component can be deactivated.
   * Return true to allow navigation, false to prompt the user.
   * @returns Boolean or CanDeactivateResult that may be wrapped in a Promise or Observable.
   */
  canDeactivate: () => MaybeAsync<CanDeactivateResult | boolean>;
}

/**
 * Configuration options for the PendingChangesGuard.
 */
export interface PendingChangesGuardConfig {
  /**
   * Function that displays a confirmation dialog to the user.
   * @param message The message to display to the user.
   * @returns Boolean indicating whether navigation should proceed.
   */
  confirmationCallback: (message: string) => MaybeAsync<boolean>;
  /**
   * Optional default message to display in the confirmation dialog.
   */
  message?: string;
}

/**
 * Converts any value type (synchronous, Promise, or Observable) to an Observable.
 * @param value The value to convert.
 * @returns An Observable wrapping the input value.
 * @internal
 */
export function toObservable<T>(value: MaybeAsync<T>): Observable<T> {
  if (isObservable(value)) {
    return value;
  }

  if (value instanceof Promise) {
    return defer(() => from(value));
  }

  return of(value as T);
}

/**
 * Injection token for configuring the PendingChangesGuard.
 */
export const PENDING_CHANGES_GUARD_CONFIG = new InjectionToken<PendingChangesGuardConfig>('PendingChangesGuardConfig');

/**
 * Default message displayed when navigating away from a component with unsaved changes.
 */
export const defaultMessage = 'WARNING: You have unsaved changes. Press Cancel to go back and save these changes, or OK to lose these changes.';

/**
 * Default configuration for the PendingChangesGuard.
 * Uses the browser's built-in confirm dialog.
 */
export const PENDING_CHANGES_GUARD_DEFAULT_CONFIG: PendingChangesGuardConfig = {
  confirmationCallback: (message: string): boolean => {
    const userConfirmed = confirm(message);
    return userConfirmed;
  },
  message: defaultMessage,
};

/**
 * Angular route guard that prevents navigation away from a component with unsaved changes.
 * 
 * This guard checks if a component has pending changes before allowing navigation away from it.
 * If changes are detected, it prompts the user with a confirmation dialog.
 * 
 * @example
 * ```typescript
 * // In app-routing.module.ts
 * const routes: Routes = [
 *   {
 *     path: 'edit',
 *     component: EditComponent,
 *     canDeactivate: [PendingChangesGuard]
 *   }
 * ];
 * 
 * // In your component
 * @Component({...})
 * export class EditComponent implements ComponentCanDeactivate {
 *   isDirty = false;
 * 
 *   // Return false to prevent navigation if there are unsaved changes
 *   canDeactivate(): boolean {
 *     return !this.isDirty;
 *   }
 * }
 * ```
 * 
 * For custom confirmation dialogs, provide a custom configuration:
 * ```typescript
 * // In app.module.ts
 * @NgModule({
 *   providers: [
 *     {
 *       provide: PENDING_CHANGES_GUARD_CONFIG,
 *       useValue: {
 *         confirmationCallback: (message) => dialogService.confirm(message)
 *       }
 *     }
 *   ]
 * })
 * ```
 */
@Injectable()
export class PendingChangesGuard implements CanDeactivate<ComponentCanDeactivate> {
  constructor(@Inject(ActivatedRoute) private route: ActivatedRoute,
    @Optional() @Inject(PENDING_CHANGES_GUARD_CONFIG) private config: PendingChangesGuardConfig) {
    // If no config is provided, use the default config
    this.config = this.config || PENDING_CHANGES_GUARD_DEFAULT_CONFIG;
  }

  /**
   * Implementation of the CanDeactivate interface.
   * Checks if the component can be deactivated using the PendingChangesCanDeactivate function.
   * 
   * @param component The component being navigated away from.
   * @returns A boolean or Observable<boolean> indicating whether navigation should proceed.
   */
  canDeactivate(component: ComponentCanDeactivate): Observable<boolean> | boolean {
    let config = this.config;
    if (!config) {
      // If no config is provided, use the default config
      config = PENDING_CHANGES_GUARD_DEFAULT_CONFIG;
    } else {
      // Merge default config with provided config
      config = { ...PENDING_CHANGES_GUARD_DEFAULT_CONFIG, ...config };
    }

    return checkPendingChanges(config, component);
  }
}

/**
 * Standalone function to check if a component can be deactivated.
 * This can be used in place of the PendingChangesGuard class for more control.
 * 
 * @param component The component implementing ComponentCanDeactivate.
 * @param _currentRoute The current activated route snapshot.
 * @param _currentState Optional current router state snapshot.
 * @param _nextState Optional next router state snapshot.
 * @returns A boolean or Observable<boolean> indicating whether navigation should proceed.
 */
export function PendingChangesCanDeactivate<T extends ComponentCanDeactivate>(
  component: T,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  _currentRoute: ActivatedRouteSnapshot,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  _currentState?: RouterStateSnapshot,
  /* eslint-disable-next-line @typescript-eslint/no-unused-vars */
  _nextState?: RouterStateSnapshot,
): Observable<boolean> | boolean {
  if (!component || !component.canDeactivate) {
    return true;
  }

  try {
    let config = inject<PendingChangesGuardConfig>(PENDING_CHANGES_GUARD_CONFIG, { optional: true });
    if (!config) {
      // If no config is provided, use the default config
      config = PENDING_CHANGES_GUARD_DEFAULT_CONFIG;
    } else {
      // Merge default config with provided config
      config = { ...PENDING_CHANGES_GUARD_DEFAULT_CONFIG, ...config };
    }

    return checkPendingChanges<T>(config, component);
  } catch (error) {
    console.error('Synchronous error in canDeactivate:', error);
    return of(false);
  }
}

function checkPendingChanges<T extends ComponentCanDeactivate>(config: PendingChangesGuardConfig, component: T) {
  let message = config.message || defaultMessage;

  const deactivationResult = component.canDeactivate();

  return toObservable(deactivationResult).pipe(
    switchMap(result => {
      // Handle both boolean and CanDeactivateResult
      if (typeof result === 'boolean') {
        return result ? of(true) : toObservable(config.confirmationCallback(message)).pipe(
          catchError(error => {
            console.error('Error in confirmation callback:', error);
            return of(false);
          })
        );
      } else {
        // It's a CanDeactivateResult
        return result.canDeactivate ? of(true) : toObservable(config.confirmationCallback(result.message ?? message)).pipe(
          catchError(error => {
            console.error('Error in confirmation callback:', error);
            return of(false);
          })
        );
      }
    }),
    catchError(error => {
      console.error('Error in canDeactivate:', error);
      return of(false);
    })
  );
}
